// Follows an engine job until it is no longer 'running'.
//
// One failed poll (a network blip, a 502 while the engine restarts after a deploy) used to
// end the loop: the dialog stopped waiting and offered its buttons again while the server
// job was still running, so the same fines could be sent twice (owner, 26 Sep 2026). A failed
// read is retried; only a job the engine no longer knows (404) or a long outage ends it.

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {() => Promise<object>} read    fetches the job once
 * @param {object} [opts]
 * @param {(job: object) => void} [opts.onUpdate]            every job read
 * @param {(error: any, failures: number) => void} [opts.onRetry]  a read failed and will be retried
 * @param {number} [opts.intervalMs=2000]
 * @param {number} [opts.maxFailures=150]   consecutive failed reads before giving up (~5 min)
 * @param {(ms: number) => Promise<void>} [opts.wait]
 * @returns {Promise<object>} the last job read (status is no longer 'running')
 */
export async function followJob(read, { onUpdate, onRetry, intervalMs = 2000, maxFailures = 150, wait = sleep } = {}) {
    let failures = 0;
    for (;;) {
        await wait(intervalMs);
        let job;
        try {
            job = await read();
        } catch (e) {
            failures += 1;
            if (e?.response?.status === 404 || failures >= maxFailures) throw e;
            if (onRetry) onRetry(e, failures);
            continue;
        }
        failures = 0;
        if (onUpdate) onUpdate(job);
        if (!job || job.status !== 'running') return job;
    }
}

export default followJob;
